<template>
  <v-container fluid class="admin-page">
    <v-row class="align-center">
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-btn icon class="mr-2" @click="goBack" title="Back to events">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h2 class="mb-0">{{ event.event_name || "Guest List" }}</h2>
            <div class="grey--text text-caption">{{ eventId }}</div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex justify-end flex-wrap">
          <v-btn class="ma-1" outlined color="primary" @click="openBroadcastDialog">
            <v-icon left small>mdi-send</v-icon> Blast
          </v-btn>
          <v-btn class="ma-1" outlined :loading="exporting" @click="exportGuests">
            <v-icon left small>mdi-file-export</v-icon> Export
          </v-btn>
          <v-btn class="ma-1" outlined @click="openImportDialog">
            <v-icon left small>mdi-file-import</v-icon> Import
          </v-btn>
          <v-btn class="ma-1" @click="fetchGuests" :loading="loading">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn class="ma-1 text-capitalize" dark @click="openCreateDialog">
            Add Guest
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="filters.q"
          label="Search name or phone"
          append-icon="mdi-magnify"
          solo
          clearable
          dense
          hide-details
          @change="fetchGuests"
        ></v-text-field>
      </v-col>
      <v-col cols="6" md="3">
        <v-select
          v-model="filters.status"
          :items="statusOptions"
          label="RSVP status"
          solo
          clearable
          dense
          hide-details
          @change="fetchGuests"
        ></v-select>
      </v-col>
      <v-col cols="6" md="3">
        <v-select
          v-model="filters.message_status"
          :items="messageStatusOptions"
          label="Message status"
          solo
          clearable
          dense
          hide-details
          @change="fetchGuests"
        ></v-select>
      </v-col>
      <v-col cols="12" md="2" class="d-flex align-center">
        <v-chip small class="mr-1">{{ summary.total }} total</v-chip>
      </v-col>
    </v-row>

    <v-row dense class="mt-1">
      <v-col cols="12">
        <v-chip
          v-for="option in messageStatusOptions"
          :key="option.value"
          small
          class="mr-1 mb-1"
          :color="messageStatusColor(option.value)"
          @click="filterByMessageStatus(option.value)"
        >
          {{ messageSummary[option.value] || 0 }} {{ option.text.toLowerCase() }}
        </v-chip>
        <v-chip small color="orange lighten-4" class="mr-1 mb-1">
          {{ summary.no_show }} no show
        </v-chip>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <Alert />
        <v-data-table
          :headers="headers"
          :items="guests"
          :loading="loading"
          loading-text="Loading guests..."
          :items-per-page="25"
          class="elevation-1"
        >
          <template v-slot:[`item.name`]="{ item }">
            <div>{{ item.designation ? `${item.designation} ` : "" }}{{ item.name }}</div>
            <div class="grey--text text-caption">{{ item.phone }}</div>
          </template>

          <template v-slot:[`item.customer_id`]="{ item }">
            <a
              :href="`/dashboard/edit/customer/${item.customer_id}`"
              target="_blank"
              rel="noopener noreferrer"
            >
              #{{ item.customer_id }}
            </a>
          </template>

          <template v-slot:[`item.message_status`]="{ item }">
            <v-chip x-small :color="messageStatusColor(item.message_status)">
              {{ messageStatusLabel(item.message_status) }}
            </v-chip>
            <v-icon
              v-if="item.last_message_error"
              x-small
              color="red"
              class="ml-1"
              :title="item.last_message_error"
            >
              mdi-alert-circle
            </v-icon>
          </template>

          <template v-slot:[`item.template_name`]="{ item }">
            <span class="text-caption">{{ guestTemplateName(item) }}</span>
          </template>

          <template v-slot:[`item.invitation_sent_at`]="{ item }">
            {{ formatDateTime(item.invitation_sent_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              :title="item.invitation_sent_at ? 'Resend invitation' : 'Send invitation'"
              :loading="Boolean(sendingGuest[item.public_id])"
              :disabled="Boolean(sendingGuest[item.public_id])"
              @click="sendInvitation(item)"
            >
              <v-icon small :color="item.invitation_sent_at ? 'grey' : 'primary'">mdi-send</v-icon>
            </v-btn>
            <v-btn icon small title="View response" @click="openResponseDialog(item)">
              <v-icon small>mdi-comment-text-outline</v-icon>
            </v-btn>
            <v-btn icon small title="Edit" @click="openEditDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small title="Remove" @click="confirmDelete(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="showFormDialog" max-width="720">
      <v-card>
        <v-card-title class="text-h6">
          {{ isEditing ? "Edit Guest" : "Add Guest" }}
        </v-card-title>

        <v-card-text>
          <v-container fluid class="px-0">
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.designation"
                  label="Salutation"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="9">
                <v-text-field
                  v-model="form.name"
                  label="Guest Name"
                  hint="Shown on the invitation; independent of the customer record's name"
                  persistent-hint
                  dense
                  outlined
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.phone"
                  label="WhatsApp Number"
                  hint="Matched against the customer list; a new customer is created if unknown"
                  persistent-hint
                  dense
                  outlined
                  :disabled="isEditing"
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="6" md="3">
                <v-text-field
                  v-model="form.pax"
                  label="Pax"
                  type="number"
                  dense
                  outlined
                ></v-text-field>
              </v-col>

              <v-col cols="6" md="3">
                <v-text-field
                  v-model="form.pax_arrive"
                  label="Pax Arrived"
                  type="number"
                  dense
                  outlined
                  :disabled="!isEditing"
                ></v-text-field>
              </v-col>

              <v-col cols="6" md="4">
                <v-text-field v-model="form.badge" label="Badge" dense outlined></v-text-field>
              </v-col>

              <v-col cols="6" md="4">
                <v-text-field v-model="form.zone" label="Zone" dense outlined></v-text-field>
              </v-col>

              <v-col cols="12" md="4" v-if="isEditing">
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  label="RSVP status"
                  dense
                  outlined
                ></v-select>
              </v-col>

              <v-col cols="12" md="6" v-if="isEditing">
                <v-select
                  v-model="form.rsvp_status"
                  :items="rsvpStatusOptions"
                  label="Response status"
                  dense
                  outlined
                  clearable
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.notes"
                  label="Notes"
                  dense
                  outlined
                  rows="2"
                ></v-textarea>
              </v-col>

              <v-col cols="12" v-if="isEditing">
                <v-divider class="mb-3"></v-divider>
                <div class="text-subtitle-2 mb-1">Template override</div>
                <div class="grey--text text-caption mb-2">
                  Leave blank to use the event's default template. The variable mapping
                  comes from the event unless you set one here.
                </div>
                <v-row dense>
                  <v-col
                    v-for="kind in messageKinds"
                    :key="kind"
                    cols="12"
                    md="4"
                  >
                    <v-text-field
                      v-model="form.templates[kind]"
                      :label="`${kindLabel(kind)} template`"
                      :placeholder="eventTemplateName(kind) || 'not set on event'"
                      persistent-placeholder
                      dense
                      outlined
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" v-if="isEditing">
                <v-textarea
                  v-model="form.guest_booking_rules"
                  label="Guest question overrides (JSON)"
                  hint='e.g. {"question_3": false} hides question 3 for this guest'
                  persistent-hint
                  outlined
                  rows="3"
                  spellcheck="false"
                  class="rules-json"
                ></v-textarea>
              </v-col>

              <v-col cols="12" v-if="isEditing && form.public_id">
                <div class="grey--text text-caption">
                  Invitation id: <strong>{{ form.public_id }}</strong>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeFormDialog">Cancel</v-btn>
          <v-btn dark :loading="saving" @click="saveGuest">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showResponseDialog" max-width="720">
      <v-card>
        <v-card-title class="text-h6">
          {{ responseGuest ? responseGuest.name : "Response" }}
        </v-card-title>
        <v-card-text>
          <div v-if="!responseAnswers.length" class="grey--text">
            This guest has not responded yet.
          </div>
          <v-simple-table v-else dense>
            <template v-slot:default>
              <tbody>
                <tr v-for="answer in responseAnswers" :key="answer.key">
                  <td class="font-weight-medium">{{ answer.question }}</td>
                  <td>{{ answer.value }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showResponseDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showBroadcastDialog" max-width="720">
      <v-card>
        <v-card-title class="text-h6">Blast Message</v-card-title>

        <v-card-text>
          <v-select
            v-model="broadcast.kind"
            :items="messageKindOptions"
            label="Message"
            dense
            outlined
            @change="loadBroadcastPreview"
          ></v-select>

          <v-switch
            v-model="broadcast.resend"
            inset
            dense
            :label="
              broadcast.resend
                ? 'Resending to everyone, including those already messaged'
                : 'Only guests who have not received this message'
            "
            @change="loadBroadcastPreview"
          ></v-switch>

          <v-text-field
            v-model="broadcast.wa_id"
            label="WhatsApp line id (optional)"
            hint="Leave blank to use the server default"
            persistent-hint
            dense
            outlined
          ></v-text-field>

          <v-alert
            v-if="broadcastPreview"
            dense
            text
            :type="broadcastPreview.count ? 'info' : 'success'"
            class="text-body-2 mt-3"
          >
            {{ broadcastPreview.message }}
          </v-alert>

          <v-alert
            v-if="broadcastResult && broadcastResult.templateSync && !broadcastResult.templateSync.ok"
            dense
            text
            type="warning"
            class="text-body-2"
          >
            {{ broadcastResult.templateSync.message }} — sends used the template list
            WhatsappAPI already had.
          </v-alert>

          <v-alert v-if="broadcastResult" dense text :type="broadcastAlertType" class="text-body-2">
            {{ broadcastResult.message }}
          </v-alert>

          <v-simple-table v-if="broadcastResult && broadcastResult.failed.length" dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="failure in broadcastResult.failed" :key="failure.public_id">
                  <td>{{ failure.name }}</td>
                  <td>{{ failure.message }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showBroadcastDialog = false">Close</v-btn>
          <v-btn
            dark
            :loading="broadcasting"
            :disabled="Boolean(broadcastPreview && !broadcastPreview.count)"
            @click="runBroadcast"
          >
            Send{{ broadcastPreview && broadcastPreview.count ? ` to ${broadcastPreview.count}` : "" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showImportDialog" max-width="720">
      <v-card>
        <v-card-title class="text-h6">Import Guest List</v-card-title>

        <v-card-text>
          <p class="text-body-2">
            Upload a .xlsx or .csv file. A row with a <strong>public_id</strong> updates that
            guest; a row with only a <strong>phone</strong> adds a new one and links it to the
            customer list. Blank cells are left unchanged; a
            <strong>-</strong> clears the field.
          </p>

          <v-btn small outlined class="mb-4" :loading="exporting" @click="downloadTemplate">
            <v-icon left small>mdi-download</v-icon> Download template
          </v-btn>

          <v-file-input
            v-model="importFile"
            label="Spreadsheet file"
            accept=".xlsx,.xls,.csv"
            dense
            outlined
            prepend-icon="mdi-paperclip"
            show-size
            truncate-length="30"
          ></v-file-input>

          <v-alert v-if="importResult" dense text :type="importAlertType" class="text-body-2">
            {{ importResult.message }}
          </v-alert>

          <v-simple-table v-if="importResult && importResult.failed.length" dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="failure in importResult.failed" :key="failure.row">
                  <td>{{ failure.row }}</td>
                  <td>{{ failure.message }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeImportDialog">Close</v-btn>
          <v-btn dark :loading="importing" :disabled="!importFile" @click="runImport">
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx";
import Alert from "@/components/Alert.vue";
import server from "../api";

const STATUS_OPTIONS = [
  { text: "Pending", value: "pending" },
  { text: "Accepted", value: "accepted" },
  { text: "Rejected", value: "rejected" },
  { text: "No Show", value: "no_show" },
];

const RSVP_STATUS_OPTIONS = [
  { text: "Recorded", value: "recorded" },
  { text: "Responded", value: "responded" },
  { text: "Messaged", value: "messaged" },
  { text: "No Message", value: "no_message" },
  { text: "Manual Messaged", value: "manual_messaged" },
];

const MESSAGE_KINDS = ["invitation", "reminder", "qr"];

const MESSAGE_STATUS_OPTIONS = [
  { text: "No message", value: "no_message" },
  { text: "Messaged", value: "messaged" },
  { text: "Responded", value: "responded" },
  { text: "Accepted", value: "accepted" },
  { text: "Rejected", value: "rejected" },
];

const MESSAGE_STATUS_COLOR = {
  no_message: "grey lighten-2",
  messaged: "blue lighten-4",
  responded: "amber lighten-4",
  accepted: "green lighten-4",
  rejected: "red lighten-4",
};

const emptyGuestTemplates = () =>
  MESSAGE_KINDS.reduce((templates, kind) => {
    templates[kind] = "";
    return templates;
  }, {});

const createDefaultForm = () => ({
  public_id: null,
  designation: "",
  name: "",
  phone: "",
  pax: "",
  pax_arrive: "",
  badge: "",
  zone: "",
  notes: "",
  status: "pending",
  rsvp_status: null,
  guest_booking_rules: "",
  templates: emptyGuestTemplates(),
});

const emptySummary = () => ({
  total: 0,
  pending: 0,
  accepted: 0,
  rejected: 0,
  no_show: 0,
});

const emptyMessageSummary = () =>
  MESSAGE_STATUS_OPTIONS.reduce((summary, option) => {
    summary[option.value] = 0;
    return summary;
  }, {});

export default {
  name: "EventGuests",
  components: {
    Alert,
  },
  data() {
    return {
      loading: false,
      saving: false,
      exporting: false,
      importing: false,
      event: {},
      guests: [],
      summary: emptySummary(),
      messageSummary: emptyMessageSummary(),
      filters: {
        q: "",
        status: null,
        message_status: null,
      },
      statusOptions: STATUS_OPTIONS,
      rsvpStatusOptions: RSVP_STATUS_OPTIONS,
      messageStatusOptions: MESSAGE_STATUS_OPTIONS,
      messageKinds: MESSAGE_KINDS,
      showFormDialog: false,
      showResponseDialog: false,
      showImportDialog: false,
      showBroadcastDialog: false,
      broadcasting: false,
      broadcast: { kind: "invitation", resend: false, wa_id: "" },
      broadcastPreview: null,
      broadcastResult: null,
      sendingGuest: {},
      editingPublicId: null,
      responseGuest: null,
      importFile: null,
      importResult: null,
      form: createDefaultForm(),
      headers: [
        { text: "#", value: "short_id", width: 70 },
        { text: "Guest", value: "name" },
        { text: "Customer", value: "customer_id", sortable: false },
        { text: "Pax", value: "pax" },
        { text: "Status", value: "message_status" },
        { text: "Template", value: "template_name", sortable: false },
        { text: "Sent", value: "invitation_sent_at" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    isEditing() {
      return Boolean(this.editingPublicId);
    },
    importAlertType() {
      if (!this.importResult) return "info";
      return this.importResult.failed.length ? "warning" : "success";
    },
    broadcastAlertType() {
      if (!this.broadcastResult) return "info";
      return this.broadcastResult.failed.length ? "warning" : "success";
    },
    messageKindOptions() {
      return MESSAGE_KINDS.map((kind) => ({ text: this.kindLabel(kind), value: kind }));
    },
    // The API returns answers already merged with their question definitions.
    responseAnswers() {
      const response = this.responseGuest && this.responseGuest.booking_response;
      if (!response || typeof response !== "object") return [];

      return Object.keys(response)
        .filter((key) => /^question_\d+$/.test(key))
        .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
        .map((key) => {
          const answer = response[key] || {};
          return {
            key,
            question: answer.question_text || key,
            value: this.formatAnswer(answer),
          };
        });
    },
  },
  methods: {
    getAuthConfig(extra = {}) {
      return {
        ...extra,
        headers: {
          token: localStorage.token,
          ...(extra.headers || {}),
        },
      };
    },
    getErrorMessage(err, fallback) {
      return (
        (err.response &&
          err.response.data &&
          err.response.data.errors &&
          (Array.isArray(err.response.data.errors)
            ? err.response.data.errors[0].message
            : err.response.data.errors.message || err.response.data.errors)) ||
        (err.response && err.response.data && err.response.data.message) ||
        fallback
      );
    },
    formatDateTime(value) {
      if (!value) return "-";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "-";
      return parsed.toLocaleString();
    },
    formatAnswer(answer) {
      const value = answer.response_value;
      if (value === null || value === undefined || value === "") return "-";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    statusColor(status) {
      if (status === "accepted") return "green lighten-4";
      if (status === "rejected") return "red lighten-4";
      if (status === "no_show") return "orange lighten-4";
      return "grey lighten-2";
    },
    messageStatusColor(messageStatus) {
      return MESSAGE_STATUS_COLOR[messageStatus] || "grey lighten-2";
    },
    messageStatusLabel(messageStatus) {
      const option = MESSAGE_STATUS_OPTIONS.find((item) => item.value === messageStatus);
      return option ? option.text : messageStatus;
    },
    filterByMessageStatus(messageStatus) {
      this.filters.message_status =
        this.filters.message_status === messageStatus ? null : messageStatus;
      this.fetchGuests();
    },
    kindLabel(kind) {
      if (kind === "qr") return "QR";
      return kind.charAt(0).toUpperCase() + kind.slice(1);
    },
    eventTemplateName(kind) {
      const templates = this.event.message_templates || {};
      return templates[kind] ? templates[kind].template_name : "";
    },
    // What this guest would actually be sent: their own override if set,
    // otherwise the event default.
    guestTemplateName(guest) {
      const own = guest.message_templates && guest.message_templates.invitation;
      if (own && own.template_name) return own.template_name;

      const fallback = this.eventTemplateName("invitation");
      return fallback ? `${fallback} (event)` : "-";
    },
    goBack() {
      this.$router.push({ name: "Events" });
    },
    activeFilterParams() {
      const params = {};
      if (this.filters.q) params.q = this.filters.q;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.message_status) params.message_status = this.filters.message_status;
      return params;
    },
    fetchGuests() {
      this.loading = true;

      const params = { ...this.activeFilterParams(), per_page: 200 };

      server
        .get(`/events/${this.eventId}/guests`, this.getAuthConfig({ params }))
        .then((response) => {
          this.event = response.data.event || {};
          this.guests = response.data.guests || [];
          this.summary = response.data.summary || emptySummary();
          this.messageSummary = response.data.message_summary || emptyMessageSummary();
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to load guest list")
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreateDialog() {
      this.editingPublicId = null;
      this.form = createDefaultForm();
      this.showFormDialog = true;
    },
    openEditDialog(item) {
      this.editingPublicId = item.public_id;
      this.form = {
        public_id: item.public_id,
        designation: item.designation || "",
        name: item.name || "",
        phone: item.phone || "",
        pax: item.pax === null || item.pax === undefined ? "" : item.pax,
        pax_arrive:
          item.pax_arrive === null || item.pax_arrive === undefined ? "" : item.pax_arrive,
        badge: item.badge || "",
        zone: item.zone || "",
        notes: item.notes || "",
        status: item.status || "pending",
        rsvp_status: item.rsvp_status || null,
        guest_booking_rules: item.guest_booking_rules
          ? JSON.stringify(item.guest_booking_rules, null, 2)
          : "",
        templates: MESSAGE_KINDS.reduce((templates, kind) => {
          const config = item.message_templates && item.message_templates[kind];
          templates[kind] = config ? config.template_name || "" : "";
          return templates;
        }, {}),
      };
      this.showFormDialog = true;
    },
    closeFormDialog() {
      this.showFormDialog = false;
      this.editingPublicId = null;
      this.form = createDefaultForm();
    },
    openResponseDialog(item) {
      this.responseGuest = item;
      this.showResponseDialog = true;
    },
    saveGuest() {
      if (!this.form.name.trim()) {
        this.$store.commit("SET_ERROR", "Guest name is required");
        return;
      }
      if (!this.isEditing && !this.form.phone.trim()) {
        this.$store.commit("SET_ERROR", "WhatsApp number is required");
        return;
      }

      let guestBookingRules;
      if (this.isEditing) {
        const raw = this.form.guest_booking_rules.trim();
        if (raw) {
          try {
            guestBookingRules = JSON.parse(raw);
          } catch (error) {
            this.$store.commit("SET_ERROR", `Invalid override JSON: ${error.message}`);
            return;
          }
        } else {
          guestBookingRules = null;
        }
      }

      this.saving = true;

      const payload = {
        name: this.form.name.trim(),
        designation: this.form.designation.trim(),
        pax: this.form.pax === "" ? null : Number(this.form.pax),
        badge: this.form.badge.trim(),
        zone: this.form.zone.trim(),
        notes: this.form.notes.trim(),
      };

      let request;
      if (this.isEditing) {
        request = server.put(
          `/events/guests/${this.editingPublicId}`,
          {
            ...payload,
            pax_arrive: this.form.pax_arrive === "" ? null : Number(this.form.pax_arrive),
            status: this.form.status,
            rsvp_status: this.form.rsvp_status || null,
            guest_booking_rules: guestBookingRules,
            message_templates: this.guestTemplatesToPayload(),
          },
          this.getAuthConfig()
        );
      } else {
        request = server.post(
          `/events/${this.eventId}/guests`,
          { ...payload, phone: this.form.phone.trim() },
          this.getAuthConfig()
        );
      }

      request
        .then(() => {
          this.$store.commit("SET_SUCCESS", this.isEditing ? "Guest updated" : "Guest added");
          this.closeFormDialog();
          this.fetchGuests();
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to save guest")
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    /**
     * A blank name means "no override" — the kind is dropped so the guest falls
     * back to the event default rather than storing an empty template.
     * The variable mapping is not repeated here; it comes from the event.
     */
    guestTemplatesToPayload() {
      const payload = {};

      MESSAGE_KINDS.forEach((kind) => {
        const name = String(this.form.templates[kind] || "").trim();
        if (!name) return;

        const eventConfig = (this.event.message_templates || {})[kind] || {};
        payload[kind] = {
          template_name: name,
          language_code: eventConfig.language_code || "id",
          header_type: eventConfig.header_type || null,
          header_media_url: eventConfig.header_media_url || null,
          header_filename: eventConfig.header_filename || null,
          variables: Array.isArray(eventConfig.variables) ? eventConfig.variables : [],
        };
      });

      return Object.keys(payload).length ? payload : null;
    },
    sendInvitation(item) {
      const resend = Boolean(item.invitation_sent_at);

      if (resend) {
        const sentAt = this.formatDateTime(item.invitation_sent_at);
        if (!window.confirm(`${item.name} was already messaged on ${sentAt}. Send again?`)) {
          return;
        }
      }

      this.$set(this.sendingGuest, item.public_id, true);

      server
        .post(
          `/events/guests/${item.public_id}/send`,
          { kind: "invitation", resend },
          this.getAuthConfig()
        )
        .then((response) => {
          this.$store.commit("SET_SUCCESS", response.data.message);
          this.fetchGuests();
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, `Failed to send invitation to ${item.name}`)
          );
        })
        .finally(() => {
          this.$delete(this.sendingGuest, item.public_id);
        });
    },
    openBroadcastDialog() {
      this.broadcast = { kind: "invitation", resend: false, wa_id: "" };
      this.broadcastResult = null;
      this.broadcastPreview = null;
      this.showBroadcastDialog = true;
      this.loadBroadcastPreview();
    },
    // Asks the API who a real send would reach, without spending messages.
    loadBroadcastPreview() {
      this.broadcastPreview = null;

      return server
        .post(
          `/events/${this.eventId}/guests/broadcast`,
          { kind: this.broadcast.kind, resend: this.broadcast.resend, dry_run: true },
          this.getAuthConfig()
        )
        .then((response) => {
          const recipients = response.data.recipients || [];
          this.broadcastPreview = {
            count: recipients.length,
            message: response.data.message,
          };
        })
        .catch((err) => {
          this.broadcastPreview = {
            count: 0,
            message: this.getErrorMessage(err, "Unable to preview recipients"),
          };
        });
    },
    runBroadcast() {
      const count = this.broadcastPreview ? this.broadcastPreview.count : 0;
      if (!window.confirm(`Send the ${this.broadcast.kind} template to ${count} guest(s)?`)) {
        return;
      }

      this.broadcasting = true;
      this.broadcastResult = null;

      server
        .post(
          `/events/${this.eventId}/guests/broadcast`,
          {
            kind: this.broadcast.kind,
            resend: this.broadcast.resend,
            wa_id: this.broadcast.wa_id.trim() || undefined,
          },
          this.getAuthConfig()
        )
        .then((response) => {
          this.broadcastResult = {
            message: response.data.message,
            failed: response.data.failed || [],
            templateSync: response.data.template_sync || null,
          };

          if (!this.broadcastResult.failed.length) {
            this.$store.commit("SET_SUCCESS", response.data.message);
          }

          this.fetchGuests();
          this.loadBroadcastPreview();
        })
        .catch((err) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(err, "Failed to send blast"));
        })
        .finally(() => {
          this.broadcasting = false;
        });
    },
    confirmDelete(item) {
      if (!window.confirm(`Remove "${item.name}" from this event?`)) return;

      server
        .delete(`/events/guests/${item.public_id}`, this.getAuthConfig())
        .then(() => {
          this.$store.commit("SET_SUCCESS", "Guest removed");
          this.fetchGuests();
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to remove guest")
          );
        });
    },
    // The API decides the columns (including one per RSVP question); this only
    // turns the rows it returns into a workbook.
    downloadSheet(params, fallbackMessage) {
      this.exporting = true;

      return server
        .get(`/events/${this.eventId}/guests/export`, this.getAuthConfig({ params }))
        .then((response) => {
          const { columns, rows, filename, sheet_name: sheetName } = response.data;
          const header = columns.map((column) => column.label);
          const body = rows.map((row) => columns.map((column) => row[column.key]));

          const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
          const workbook = XLSX.utils.book_new();

          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || "Guest List");
          XLSX.writeFile(workbook, filename);

          this.$store.commit("SET_SUCCESS", `${rows.length} row(s) exported`);
        })
        .catch((err) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(err, fallbackMessage));
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    exportGuests() {
      return this.downloadSheet(this.activeFilterParams(), "Failed to export guest list");
    },
    downloadTemplate() {
      return this.downloadSheet({ template: "true" }, "Failed to download template");
    },
    openImportDialog() {
      this.importFile = null;
      this.importResult = null;
      this.showImportDialog = true;
    },
    closeImportDialog() {
      this.showImportDialog = false;
      this.importFile = null;
      this.importResult = null;
    },
    readSheetRows(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(new Error("Unable to read the file"));
        reader.onload = (event) => {
          try {
            const workbook = XLSX.read(new Uint8Array(event.target.result), {
              type: "array",
            });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            // defval keeps blank cells present, so the API can tell "unchanged"
            // from a column that was never in the file.
            resolve(XLSX.utils.sheet_to_json(firstSheet, { defval: "" }));
          } catch (error) {
            reject(error);
          }
        };

        reader.readAsArrayBuffer(file);
      });
    },
    async runImport() {
      if (!this.importFile) return;

      this.importing = true;
      this.importResult = null;

      try {
        const rows = await this.readSheetRows(this.importFile);

        if (!rows.length) {
          this.$store.commit("SET_ERROR", "The file has no rows");
          return;
        }

        const { data } = await server.post(
          `/events/${this.eventId}/guests/import`,
          { rows },
          this.getAuthConfig()
        );

        this.importResult = {
          message: data.message,
          failed: data.failed || [],
        };

        if (!this.importResult.failed.length) {
          this.$store.commit("SET_SUCCESS", data.message);
        }

        this.fetchGuests();
      } catch (err) {
        this.$store.commit("SET_ERROR", this.getErrorMessage(err, "Failed to import file"));
      } finally {
        this.importing = false;
      }
    },
  },
  created() {
    this.fetchGuests();
  },
};
</script>

<style scoped>
a {
  color: inherit;
}

.rules-json >>> textarea {
  font-family: "Menlo", "Consolas", monospace;
  font-size: 12px;
  line-height: 1.5;
}
</style>
